import * as moment from 'moment-timezone';
import {useEffect, useState} from "react";
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {Calendar, momentLocalizer, Views} from 'react-big-calendar';
import {Backdrop, Box, CircularProgress, Divider, LinearProgress} from "@mui/material";
import {blue, grey} from "@mui/material/colors";
import {useLazyQuery} from "@apollo/client";

import {useTimezone} from '../hooks/useTimezone';
import useFirstDayOfTheWeek, {DEFAULT_START_DAY} from '../hooks/useFirstDayOfTheWeek';
import SchedulePlannerToolbar from '../components/shift-plans/SchedulePlannerToolbar';
import ShiftPlanCard from '../components/shift-plans/ShiftPlanCard';
import {ALL_SERVICE_TYPES, GET_BOOKING_STATS, GET_DETAILED_SHIFT_PLANS, GET_SHIFT_PLANS} from "../queries/GraphQLQueries";
import {useQuery} from "@apollo/client";
import ServiceTypeTitle from '../components/common/ServiceTypeTitle';
import SchedulePlannerPerformerMetricCellWrapper from '../components/shift-plans/SchedulePlannerPerformerMetricCellWrapper';
import {retrieveServiceTypeIcon} from '../helpers/ServiceTypeUtils';
import {processShiftPlanData} from '../helpers/ShiftPlanHelper';
import {SCHEDULE_PLANNER_VIEWS} from "../constants/SchedulePlannerConstants";
import AddShiftPlanDialog from "../components/shift-plans/AddShiftPlanDialog";
import CustomAlert from "../components/CustomAlert";

const TIME_GROUPING_INTERVAL = 'DAILY';

const DARK_BLUE = blue[700];

const styles = {
    refreshProgressContainer: {
        my: '0.25rem',
        height: '0.25rem'
    },
    cardsContainer: {
        backgroundColor: grey[200],
        height: '100%',
        padding: '1rem',
        display: 'flex',
        flexWrap: 'nowrap',
        gap: '1rem',
        overflowX: 'auto',
        overflowY: 'hidden',
        width: '100%',
        flex: 1,
        minHeight: 0
    },
    cardItem: {
        flex: '0 0 auto',
        width: 'fit-content',
        minWidth: 'fit-content',
        height: '100%',
    },
    monthCalendarContainer: {
        paddingX: '1rem',
        paddingBottom: '1rem',
        height: 'calc(100vh - 12rem)',
        overflowY: 'auto',
    },
    monthCalendar: {
        height: '100%',
        minHeight: '52rem'
    },
    monthLoadingBackdrop: {
        color: '#fff',
        zIndex: 1300
    }
}
export default function SchedulePlanner() {

    const {timezone} = useTimezone();
    useFirstDayOfTheWeek(DEFAULT_START_DAY);
    const [currentDate, setCurrentDate] = useState(moment());
    const [currentView, setCurrentView] = useState(SCHEDULE_PLANNER_VIEWS.MONTH);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [addShiftPlanDialogOpen, setAddShiftPlanDialogOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState(null);
    const [selectedServiceTypeKeycodes, setSelectedServiceTypeKeycodes] = useState([]);

    const {data: serviceTypesData} = useQuery(ALL_SERVICE_TYPES);
    const serviceTypeOptions = serviceTypesData?.serviceTypes || [];

    const effectiveSelectedServiceTypeKeycodes = selectedServiceTypeKeycodes.length > 0
        ? selectedServiceTypeKeycodes
        : serviceTypeOptions.map((serviceType) => serviceType.keycode);

    const [fetchShiftPlansForMonthView, {data: shiftPlansForMonthViewData, loading: isLoadingMonthShiftPlans}] = useLazyQuery(GET_SHIFT_PLANS, {
        fetchPolicy: 'network-only'
    });
    const [fetchShiftPlansForDayView, {data: shiftPlansForDayViewData}] = useLazyQuery(GET_DETAILED_SHIFT_PLANS, {
        fetchPolicy: 'network-only'
    });
    const [fetchBookingStats, {data: bookingStatsData, loading: isLoadingBookingStats}] = useLazyQuery(GET_BOOKING_STATS, {fetchPolicy: 'network-only'});

    useEffect(() => {
        const queryVariables = {
            startTime: moment(currentDate).tz(timezone).startOf(currentView).toISOString(),
            endTime: moment(currentDate).tz(timezone).endOf(currentView).toISOString()
        };

        if (currentView === SCHEDULE_PLANNER_VIEWS.MONTH) {
            fetchShiftPlansForMonthView({
                variables: queryVariables
            });

            return;
        }

        fetchShiftPlansForDayView({
            variables: {
                ...queryVariables
            }
        });
    }, [currentDate, timezone, currentView, fetchShiftPlansForMonthView, fetchShiftPlansForDayView]);

    useEffect(() => {
        fetchBookingStats({
            variables: {
                serviceTypeKeycode: process.env.REACT_APP_SCHEDULE_METRIC_SERVICE_TYPE,
                from: moment(currentDate).tz(timezone).startOf(currentView).toISOString(),
                to: moment(currentDate).tz(timezone).endOf(currentView).toISOString(),
                grouping: TIME_GROUPING_INTERVAL,
                timezone: timezone
            }
        });
    }, [currentDate, timezone, currentView, fetchBookingStats]);

    const shiftPlansForDay = shiftPlansForDayViewData?.shiftPlans || [];
    const shiftPlansForMonth = shiftPlansForMonthViewData?.shiftPlans || [];

    const filteredShiftPlansForDay = shiftPlansForDay.filter((shiftPlan) => {
        if (effectiveSelectedServiceTypeKeycodes.length === 0) {
            return true;
        }

        const serviceTypePlans = shiftPlan.bookableServiceTypePlans || [];

        return serviceTypePlans.some((plan) => effectiveSelectedServiceTypeKeycodes.includes(plan.serviceType?.keycode));
    });

    const isMonthLoading = currentView === SCHEDULE_PLANNER_VIEWS.MONTH && (isLoadingMonthShiftPlans || isLoadingBookingStats);
    const {mappedShiftPlans, shiftPlansByDateMap, performerMetricsByDate} = processShiftPlanData({shiftPlans: shiftPlansForMonth}, timezone);
    const localizer = momentLocalizer(moment);

    const shiftPlanEventsForMonth = mappedShiftPlans.map((shiftPlan) => {

        return {
            id: shiftPlan.id,
            title: (
                <ServiceTypeTitle
                    title={shiftPlan.title}
                    serviceTypeIcons={retrieveServiceTypeIcon(shiftPlan.serviceTypeKeycodes)}
                    serviceTypeDescriptions={shiftPlan.serviceTypeDescriptions}
                />
            ),
            start: shiftPlan.start,
            end: shiftPlan.end,
            enrollments: shiftPlan.enrollments || []
        };
    });

    const handleRefresh = () => {
        setIsRefreshing(true);
        setCurrentDate(previousDate => moment(previousDate).tz(timezone));
    };

    useEffect(() => {
        if (!isRefreshing) {
            return;
        }

        setIsRefreshing(false);
    }, [currentDate, isRefreshing]);

    const handleToolbarDateChange = (nextDate) => {
        if (!nextDate) {
            return;
        }

        setCurrentDate(moment(nextDate).tz(timezone));
    };

    const handleViewChange = (_event, selectedView) => {
        if (!selectedView) {
            return;
        }

        setCurrentView(selectedView);
    };

    const handleServiceTypeChange = (event) => {
        const updatedServiceTypeKeycodes = event.target.value;

        if (updatedServiceTypeKeycodes.length === 0) {
            return;
        }

        setSelectedServiceTypeKeycodes(updatedServiceTypeKeycodes);
    };

    const handleGoPrevious = () => {
        setCurrentDate(previousDate => moment(previousDate).tz(timezone).subtract(1, currentView));
    };

    const handleGoNext = () => {
        setCurrentDate(previousDate => moment(previousDate).tz(timezone).add(1, currentView));
    };

    const handleMonthViewNavigate = (date) => {
        setCurrentDate(moment(date).tz(timezone));
    };

    const handleMonthViewSlotSelection = ({start}) => {
        setCurrentDate(moment(start).tz(timezone));
        setCurrentView(SCHEDULE_PLANNER_VIEWS.DAY);
    };

    const handleShiftPlanAdded = () => {
        setAddShiftPlanDialogOpen(false);
        setAlertMessage({children: 'Shift plan created successfully.', severity: 'success'});
        handleRefresh();
    };

    const handleCloseCustomAlert = () => {
        setAlertMessage(null);
    };

    const sessionStyle = (event) => {
        let customBorderStyle = `0.07rem solid ${DARK_BLUE}`;
        let customBackgroundColor = 'white';
        let fontColor = 'black';

        if (event.enrollments.some(enrollment => enrollment.availability !== null)) {
            customBackgroundColor = DARK_BLUE;
            fontColor = 'white';
        }

        let timeslotBackgroundStyle = {
            backgroundColor: customBackgroundColor,
            border: customBorderStyle,
            color: fontColor,
        };

        return {className: "", style: timeslotBackgroundStyle};
    };

    return (
        <Box sx={{height: 'calc(100vh - 8.5rem)'}}>
            <SchedulePlannerToolbar
                onRefresh={handleRefresh}
                currentDate={currentDate}
                onDateChange={handleToolbarDateChange}
                onGoPrevious={handleGoPrevious}
                onGoNext={handleGoNext}
                availableServiceTypes={serviceTypeOptions}
                activeView={currentView}
                onViewChange={handleViewChange}
                selectedServiceTypeKeycodes={effectiveSelectedServiceTypeKeycodes}
                onServiceTypeChange={handleServiceTypeChange}
                setAddShiftPlanDialogOpen={setAddShiftPlanDialogOpen}/>
            <Box sx={styles.refreshProgressContainer}>
                {
                    isRefreshing && <LinearProgress/>
                }
            </Box>
            <Divider/>

            {
                currentView === SCHEDULE_PLANNER_VIEWS.MONTH
                    ?
                    <Box className="schedule-planner-calendar" sx={styles.monthCalendarContainer}>
                        <Calendar
                            localizer={localizer}
                            popup
                            defaultView={Views.MONTH}
                            view={Views.MONTH}
                            date={moment(currentDate).toDate()}
                            events={shiftPlanEventsForMonth}
                            style={styles.monthCalendar}
                            eventPropGetter={sessionStyle}
                            onNavigate={handleMonthViewNavigate}
                            onSelectSlot={handleMonthViewSlotSelection}
                            tooltipAccessor={null}
                            selectable
                            views={[Views.MONTH]}
                            toolbar={false}
                            components={{
                                dateCellWrapper: (wrapperProps) => (
                                    <SchedulePlannerPerformerMetricCellWrapper
                                        {...wrapperProps}
                                        currentDate={currentDate}
                                        timezone={timezone}
                                        shiftPlansByDateMap={shiftPlansByDateMap}
                                        bookingStatsData={bookingStatsData}
                                        performerMetricsByDate={performerMetricsByDate}
                                    />
                                )
                            }}/>
                    </Box>
                    :

                    <Box sx={styles.cardsContainer}>
                        {
                            filteredShiftPlansForDay.length === 0
                                ?
                                <Box key="no-shift-plan" sx={styles.cardItem}>
                                    <ShiftPlanCard
                                        currentDate={currentDate}
                                        shouldRefreshShiftPlans={handleRefresh}
                                        inputShiftPlans={filteredShiftPlansForDay}
                                        isLoadingShiftPlans={!shiftPlansForDayViewData}/>
                                </Box>
                                :
                                filteredShiftPlansForDay.map((shiftPlan) => (
                                    <Box key={shiftPlan.id} sx={styles.cardItem}>
                                        <ShiftPlanCard
                                            currentDate={currentDate}
                                            shouldRefreshShiftPlans={handleRefresh}
                                            shiftPlanId={shiftPlan.id}
                                            inputShiftPlans={filteredShiftPlansForDay}
                                            isLoadingShiftPlans={!shiftPlansForDayViewData}/>
                                    </Box>
                                ))
                        }
                    </Box>


            }

            <AddShiftPlanDialog
                openAddShiftPlanDialog={addShiftPlanDialogOpen}
                handleClose={()=>setAddShiftPlanDialogOpen(false)}
                onShiftPlanAdded={handleShiftPlanAdded}
                currentDate={currentDate}/>

            <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseCustomAlert} />

            <Backdrop open={isMonthLoading} sx={styles.monthLoadingBackdrop}>
                <CircularProgress color="inherit"/>
            </Backdrop>
        </Box>
    );
};